'use client';

import { Atom, atom as atomJotai, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, atomWithStorage, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { LOCAL_STORAGE } from 'lib/constants/local-storage';

import { DebugMessageColor, colorOption, locColor } from 'utils/debugger';

/**
 * Keys for managing areas for logging
 */
export enum RosBridgeLogging {
  SERVICE_REQUEST = 'ros:service:request',
  SERVICE_RESPONSE = 'ros:service:response',
  ACTION_GOAL = 'ros:action:goal',
  ACTION_FEEDBACK = 'ros:action:feedback',
  ACTION_RESULT = 'ros:action:result',
}

export const ROS_BRIDGE_LOGGING = Object.values(RosBridgeLogging);

type DebugRosBridgeStateEntry = {
  log: boolean;
  color: string;
};

type DebugRosBridgeState = Record<RosBridgeLogging, DebugRosBridgeStateEntry>;

const getLogRosBridgeDefaultState = (): DebugRosBridgeState => {
  const stored =
    typeof window !== 'undefined' ? localStorage?.getItem(LOCAL_STORAGE.DEBUG.ROS_BRIDGE) : null;
  const state = stored ? JSON.parse(stored) : {};

  ROS_BRIDGE_LOGGING.forEach((key) => {
    if (!state[key]) {
      state[key] = {
        log: false,
        color: colorOption[DebugMessageColor.BLUE],
      };
    }
  });

  return state;
};

const logRosBridgeAtom = atomWithStorage('logRosBridgeAtom2', getLogRosBridgeDefaultState());
logRosBridgeAtom.debugLabel = 'Debug/Log Ros Bridge Atom';

const logRosBridgeSelector = atomFamily<RosBridgeLogging, Atom<DebugRosBridgeStateEntry>>(
  (topic) => {
    const innerAtom = atomJotai((get) => get(logRosBridgeAtom)[topic]);
    innerAtom.debugLabel = 'Debug/Log Ros Bridge Selector';
    return innerAtom;
  },
);

export function useLogRosBridgeSetter(key: RosBridgeLogging) {
  const { log, color } = useAtomValue(logRosBridgeSelector(key));
  const setDebugState = useSetAtom(logRosBridgeAtom);

  const updateLog = (log: boolean) => {
    setDebugState((old) => {
      const newItem = {
        ...old[key],
        log,
      };
      return {
        ...old,
        [key]: newItem,
      };
    });
  };

  const updateColor = (color: string) => {
    setDebugState((old) => {
      const newItem = {
        ...old[key],
        color,
      };
      return {
        ...old,
        [key]: newItem,
      };
    });
  };

  return { color, log, updateLog, updateColor } as const;
}

/**
 * Returns a callback to read the state without subscribing to it.
 */
function useDebugRosBridgeCallback() {
  const getDebugRosBridge = useAtomCallback(
    useCallback((get) => (key: RosBridgeLogging) => get(logRosBridgeSelector(key)), []),
  );

  return { getDebugRosBridge };
}

/**
 * Returns a callback to read the state without subscribing to it.
 */
export function useLogRosBridge() {
  const { getDebugRosBridge } = useDebugRosBridgeCallback();

  function logRosBridge(topic: RosBridgeLogging, text: string, ...rest: unknown[]) {
    const debug = getDebugRosBridge()(topic);

    if (debug?.log) {
      locColor(debug.color, text, ...rest);
    }
  }

  return { logRosBridge };
}

export type LogRosBridge = ReturnType<typeof useLogRosBridge>['logRosBridge'];
